import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { useGetCatalogQuery, useGetReleaseKitQuery, useGetReleasesQuery } from '../../../../redux/api/fymAPI';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useQueryParam } from '../../../../utils/hooks/useQueryParam';
import { PromoteContainer } from '../PromoteContainer';
import { smlNavigationMessages } from '../../navigation/smlNavigationMessages';
import { ReleaseHeader } from './ReleaseHeader';
import { ReleaseTabs } from './ReleaseTabs';
import { ReleaseNotFound } from './ReleaseNotFound';
import { ReleaseTab, releaseTabPanelId } from './releaseTabProps';
import { releaseTabFigures, resolveReleaseTab } from './releaseTabFigures';
import { parseReleaseFocus } from './releasePageLink';
import { ThemeLeaveDialog } from './theme/ThemeLeaveDialog';
import { isSameReleasePage } from './theme/isSameReleasePage';
import { To, useLeaveGuard } from '../../../smartLinks/catalog/song/page/useLeaveGuard';
import { useKitZip } from './assets/useKitZip';
import { useKitNavigation } from './assets/useKitNavigation';
import { ReleaseTabBody } from './ReleaseTabBody';
import { ReleaseEmailLinks } from './ReleaseEmailLinks';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './ReleasePage.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReleasePageProps {
  accountId: number;
}

/**
 * The release page of one catalog song (Releases v2, design b, c, f; assets-rethink b1):
 * `promoteRelease?catalogItemUuid=<uuid>[&tab=overview|kit|posts|plan|campaign][&asset=<uuid>&rev=<n>]`.
 * The head, then five tabs: Overview (setup, done, planned, content made, can still make, theme), Kit
 * (the keepers as a fixed slot grid), Posts (the post files of the song, a filtered Posts board),
 * Release plan (the rollout or the push) and Auto campaign (the AI reels of the song, one on each day). No tab opens Overview; `focus=aiReel` opens Posts; the old
 * `tab=assets` opens Kit or Posts by the kind of its `asset`. The page has no setup form: the setup
 * lives on the Catalog song page (owner rule 2026-10-06).
 */
const ReleasePageComponent: React.FC<ReleasePageProps> = ({ accountId }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const catalogItemUuid = useQueryParam('catalogItemUuid', undefined, '');
  const hasFocus = parseReleaseFocus(useQueryParam('focus', undefined, '')) !== undefined;
  const tabParam = useQueryParam('tab', undefined, '');
  const assetParam = useQueryParam('asset', undefined, '');
  const catalog = useGetCatalogQuery({ accountId });
  const item = useMemo(
    () => catalog.data?.items.find(({ uuid }) => uuid === catalogItemUuid),
    [catalog.data, catalogItemUuid]
  );
  const release = useGetReleaseKitQuery({ accountId, catalogItemUuid }, { skip: item === undefined });
  // design: assets-rethink d1. The old `tab=assets` opens Kit, or Posts for a post file.
  const tab: ReleaseTab = resolveReleaseTab({ tab: tabParam, hasFocus, asset: assetParam, release: release.data });
  // The Theme panel holds a draft until Save: a leave (another page, another song or another tab)
  // asks first.
  const [isThemeDirty, setIsThemeDirty] = useState(false);
  const isSamePage = useCallback((to: To) => isSameReleasePage(to, catalogItemUuid, tab), [catalogItemUuid, tab]);
  const guard = useLeaveGuard(isThemeDirty, isSamePage);
  const releases = useGetReleasesQuery({ accountId }, { skip: item === undefined });
  const row = useMemo(
    () => releases.data?.releases.find((one) => one.catalogItemUuid === catalogItemUuid),
    [catalogItemUuid, releases.data]
  );
  const zip = useKitZip({ accountId, catalogItemUuid });

  const back = useCallback(() => router.push(SMLRoutes.promote), [router]);
  const changeTab = useCallback(
    (next: ReleaseTab) => router.push(SMLRoutes.promoteRelease, { catalogItemUuid, tab: next }),
    [catalogItemUuid, router]
  );
  const { refetch: refetchCatalog } = catalog;
  const retryCatalog = useCallback(() => {
    refetchCatalog().catch(console.error);
  }, [refetchCatalog]);
  const { refetch: refetchRelease } = release;
  const retryRelease = useCallback(() => {
    refetchRelease().catch(console.error);
  }, [refetchRelease]);

  const releasesLabel = intl.formatMessage(smlNavigationMessages.promoteReleases);
  const releaseData = release.data;
  const figures = useMemo(
    () => (item === undefined ? { kit: null, posts: null } : releaseTabFigures(item, releaseData)),
    [item, releaseData]
  );
  const hasReadyAsset = useMemo(
    () =>
      releaseData !== undefined &&
      (releaseData.assets.some((asset) => asset.status === 'ready') ||
        releaseData.reels.some((reel) => reel.status === 'ready' || reel.status === 'approved')),
    [releaseData]
  );

  if (item === undefined) {
    const body = catalog.isLoading ? (
      <LoadingView />
    ) : (
      <ReleaseNotFound error={catalog.error} retry={retryCatalog} onBack={back} />
    );
    return <PromoteContainer title={releasesLabel}>{body}</PromoteContainer>;
  }

  let body: React.ReactNode;
  if (release.isLoading) {
    body = <LoadingView />;
  } else if (releaseData === undefined) {
    body = <ReleaseNotFound error={release.error} retry={retryRelease} onBack={back} />;
  } else {
    body = (
      <ReleaseTabBody
        tab={tab}
        accountId={accountId}
        item={item}
        release={releaseData}
        onThemeDirtyChange={setIsThemeDirty}
        onChangeTab={changeTab}
      />
    );
  }

  return (
    <PromoteContainer title={item.title} crumbs={[releasesLabel, item.title]}>
      <ReleaseHeaderWithStudio
        accountId={accountId}
        item={item}
        themeId={releaseData?.kit?.look.themeId ?? null}
        hasReadyAsset={hasReadyAsset}
        zip={zip}
      />
      <ReleaseTabs
        tab={tab}
        kitFigure={figures.kit}
        postCount={figures.posts}
        plannedCount={row?.posts.planned ?? null}
        onChange={changeTab}
      />
      <div
        id={releaseTabPanelId(tab)}
        role="tabpanel"
        aria-labelledby={`${releaseTabPanelId(tab)}-tab`}
        className={styles.tabPanel}
      >
        {body}
      </div>
      {releaseData !== undefined && <ReleaseEmailLinks item={item} assets={releaseData.assets} zip={zip} />}
      <ThemeLeaveDialog isOpen={guard.isBlocked} title={item.title} onLeave={guard.proceed} onStay={guard.cancel} />
    </PromoteContainer>
  );
};

/** The head with "Open Studio" on the song (the Studio opens with the song picked). */
const ReleaseHeaderWithStudio: React.FC<Omit<React.ComponentProps<typeof ReleaseHeader>, 'onOpenStudio'>> = ({
  accountId,
  item,
  themeId,
  hasReadyAsset,
  zip,
}) => {
  const navigation = useKitNavigation(item);
  return (
    <ReleaseHeader
      accountId={accountId}
      item={item}
      themeId={themeId}
      hasReadyAsset={hasReadyAsset}
      zip={zip}
      onOpenStudio={navigation.openStudio}
    />
  );
};

export const ReleasePage = TypedMemo(ReleasePageComponent);
